
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  UserIcon, 
  XCircleIcon, 
  AcademicCapIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import api from '../../../services/authService'
import { toast, Toaster } from 'react-hot-toast'

interface AlumnoResultado {
  id_intento: number | null
  id_usuario: number
  nombre: string
  email: string
  estado: string
  progreso: number
  puntaje: number | null
  retroalimentacion: string | null
  fecha_calificacion: string | null
  fecha_inicio: string | null
  fecha_fin: string | null
}

interface AsignacionInfo {
  id: number
  simulacion: string
  grupo: string
}

const PaginaEvaluacionSimulacion: React.FC = () => {
  const { idAsignacion } = useParams<{ idAsignacion: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [asignacion, setAsignacion] = useState<AsignacionInfo | null>(null)
  const [alumnos, setAlumnos] = useState<AlumnoResultado[]>([])
  
  // Estado para el modal de calificación
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState<AlumnoResultado | null>(null)
  const [calificacion, setCalificacion] = useState<number | ''>('')
  const [retroalimentacion, setRetroalimentacion] = useState('')
  const [modalOpen, setModalOpen] = useState(false)

  const cargarDatos = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/api/simulaciones/asignaciones/${idAsignacion}/resultados`)
      setAsignacion(res.data.asignacion)
      setAlumnos(res.data.alumnos)
    } catch (error) {
      console.error(error)
      toast.error('Error al cargar resultados')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (idAsignacion) {
      cargarDatos()
    }
  }, [idAsignacion])

  const abrirCalificar = (alumno: AlumnoResultado) => {
    setAlumnoSeleccionado(alumno)
    setCalificacion(alumno.puntaje !== null ? alumno.puntaje : '')
    setRetroalimentacion(alumno.retroalimentacion || '')
    setModalOpen(true)
  }

  const guardarCalificacion = async () => {
    if (!alumnoSeleccionado || !alumnoSeleccionado.id_intento) return
    
    try {
      await api.put(`/api/simulaciones/intentos/${alumnoSeleccionado.id_intento}/calificar`, {
        puntaje: Number(calificacion),
        retroalimentacion
      })
      toast.success('Calificación guardada exitosamente')
      setModalOpen(false)
      cargarDatos() // Recargar lista
    } catch (error) {
      console.error(error)
      toast.error('Error al guardar calificación')
    }
  }

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <Toaster position="top-right" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-500 hover:text-gray-700 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Volver
          </button>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Evaluación de Práctica Profesional
                </h1>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Caso Práctico:</span> {asignacion?.simulacion}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Grupo:</span> {asignacion?.grupo}
                </p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-lg">
                <span className="block text-xs font-bold uppercase">Total Alumnos</span>
                <span className="text-2xl font-bold">{alumnos.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Alumnos */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alumno</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calificación</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {alumnos.map((alumno) => (
                <tr key={alumno.id_usuario} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-gray-500" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {alumno.nombre || 'Sin Nombre'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {alumno.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${alumno.estado === 'completado' ? 'bg-green-100 text-green-800' : 
                        alumno.estado === 'en_curso' ? 'bg-yellow-100 text-yellow-800' : 
                        alumno.estado === 'calificado' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                      {alumno.estado === 'calificado' ? 'Calificado' : 
                       alumno.estado === 'completado' ? 'Completado' : 
                       alumno.estado === 'en_curso' ? 'En Curso' : 'No Iniciado'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 max-w-[100px]">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${alumno.progreso}%` }}></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-1 block">{alumno.progreso}%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {alumno.puntaje !== null ? (
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        {alumno.puntaje}/100
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {alumno.id_intento ? (
                      <button 
                        onClick={() => abrirCalificar(alumno)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center justify-end gap-1 ml-auto"
                      >
                        <AcademicCapIcon className="h-5 w-5" />
                        Calificar
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs italic">Sin intentos</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Calificación */}
      {modalOpen && alumnoSeleccionado && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6 animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <AcademicCapIcon className="h-6 w-6 text-indigo-600" />
                Calificar Alumno
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Alumno:</p>
              <p className="font-medium text-gray-900 dark:text-white text-lg">{alumnoSeleccionado.nombre}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Calificación (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={calificacion}
                  onChange={(e) => setCalificacion(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Retroalimentación
                </label>
                <textarea
                  rows={4}
                  value={retroalimentacion}
                  onChange={(e) => setRetroalimentacion(e.target.value)}
                  placeholder="Escriba comentarios para el alumno..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarCalificacion}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm transition-colors"
              >
                Guardar Calificación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaginaEvaluacionSimulacion
